import React from 'react';
import {
  Popover, Button, Tooltip, message, Icon, Select,
} from 'antd';
import {
  isEmpty, isEqual, mapValues, find, sortBy,
} from 'lodash';
import { CompactPicker } from 'react-color';
import { isObject } from 'util';
import {
  Fields, popup, popupChild, Save, fieldsWidth, EmptyField,
} from './styles';
import ValueEditor from './ValueEditor';
import { TYPES, EDITABLE_TYPES, HIDDEN_COLUMNS } from '../../constants/grid.constants';
import {
  snakeToCamel, objectToString, predicatesToText, validateRow,
} from '../../helpers';
import {
  BULK_EDIT_SUCCESS, ADD_SUCCESS, IS_DUPLICATE, IS_NOT_FULL, IS_EMPTY,
} from '../../constants/text.constants';

const hideColumns = columns => columns
  .filter(({ name, type }) => (type !== TYPES.child) && !HIDDEN_COLUMNS.includes(name));

const isFull = (payload, columns) => {
  // The list of columns that are required by a Bulk Editor.
  const requiredColumns = columns
    .filter(({ required }) => required)
    .map(({ name }) => name)
    .sort();

  // The list of columns that are being provided to a Bulk Editor.
  const requiredPayload = Object.keys(payload)
    .filter(key => requiredColumns.includes(key))
    .sort();

  return isEqual(requiredColumns, requiredPayload);
};

const buttonText = ({
  modification, displayValue, value, modifier_unit,
}, lookups) => {
  const lookup = find(lookups, { id: value });
  if (modification === 'set') {
    return `${(lookup && lookup.name) || (isObject(value[0])
        ? objectToString(value[0])
        : (displayValue || value))
      }`;
  }
  return `${modification} ${displayValue || value} ${modifier_unit || ''}`;
};

const Popup = ({
  isChild,
  adding,
  siblings,
  style,
  icon,
  onClick,
  meta: { name, display, required },
  popoverProps: { text, visible },
  component,
}) => (
  <Popover
    visible={name === visible}
    placement="bottom"
    trigger={['click', 'focus']}
    title={display}
    content={component}
    overlayClassName={`${isChild && popupChild(siblings)} ${popup}`}
  >
    <Tooltip title={`${display} ${(adding && required) ? '(Required)' : ''}`}>
      <Button style={style} onClick={() => onClick(name)}>
        {icon}
        {text}
      </Button>
    </Tooltip>
  </Popover>
);

const editingAllowed = type => EDITABLE_TYPES[type];

const Field = ({
  icon,
  text,
  onEditChange,
  meta,
  style,
  adding,
  lookups,
  hideAll,
  onClick,
  visible,
}) => {
  const notBulkEditable = !adding && !(editingAllowed(meta.type) && meta.editable)
  if (notBulkEditable) {
    return (
      <Button title={`${meta.display} (Disabled)`} disabled {...{ style }}>
        {icon}
        {text}
      </Button>
    );
  }

  if (meta.type === TYPES.lookup) {
    const currentLookups = lookups[snakeToCamel(meta.lookup_type)] || [];
    const sortedLookups = sortBy(currentLookups, meta.lookup_type === 'day_of_week'
      ? 'id'
      : 'name');
    return (
      <Popup
        {...{
          style, icon, adding, meta, onClick,
        }}
        popoverProps={{ text, visible }}
        component={(
          <Select
            showSearch
            style={{ width: 300, padding: 16 }}
            placeholder="Search for options"
            onChange={(value) => {
              onEditChange(meta.name, {
                modification: 'set',
                value,
                modifier_unit: 'number', // TODO: WHY??? I don't think is needed. May need to remove it.
              });
              hideAll();
            }}

            filterOption={(input, option) => (
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            )}
          >
            {sortedLookups.sort().map(({ id, name, ...nestedFields }) => {
              // Include nested fields of the LOOKUP_NESTED_FIELDS object (e.g. employee.sap_id)
              const nestedValues = Object.values(nestedFields).filter(v => v != null && v !== '');
              const label = nestedValues.length > 0 ? `${name} (${nestedValues.join(', ')})` : name;
              return (
                <Select.Option key={name} value={id}>
                  {label}
                </Select.Option>
              );
            })}
          </Select>
        )}
      />
    );
  }

  if (meta.type === TYPES.colour) {
    return (
      <Popup
        {...{
          style, icon, adding, meta, onClick,
        }}
        popoverProps={{ text, visible }}
        component={(
          <CompactPicker
            color={text}
            triangle="hide"
            onChange={({ hex }) => {
              onEditChange(meta.name, {
                modification: 'set',
                value: hex,
                modifier_unit: 'number',
              });
              hideAll();
            }}
          />
        )}
      />
    );
  }

  return (
    <Popup
      {...{
        style, icon, adding, meta, onClick,
      }}
      popoverProps={{ text, visible }}
      component={(
        <ValueEditor
          setOnly={meta.type === TYPES.string || adding}
          dataType={meta.type}
          displayName={meta.display}
          onSave={(edit) => {
            hideAll();
            onEditChange(meta.name, edit);
          }}
        />
      )}
    />
  );
};

class BulkEditor extends React.Component {
  state = {
    toEdit: {},
    visible: '',
  };

  clearEdit = () => this.setState({
    toEdit: {},
  })

  onEditChange = (key, value) => {
    const { toEdit } = this.state;
    const { meta, lookups } = this.props;

    // When a lookup field is selected, auto-populate any sibling fields whose
    // names match nested fields of the looked-up object (e.g. employee.sap_id).
    // Nested fields are declared in LOOKUP_NESTED_FIELDS on the backend and
    // included in the lookup payload alongside id and name.
    const derivedUpdates = {};
    if (value.modification === 'set') {
      const column = find(meta.currentMeta.columns, { name: key });
      if (column && column.type === TYPES.lookup && column.lookup_type) {
        const selectedItem = find(lookups[snakeToCamel(column.lookup_type)], { id: value.value });
        if (selectedItem) {
          meta.currentMeta.columns.forEach(({ name }) => {
            if (name !== key && Object.prototype.hasOwnProperty.call(selectedItem, name) && selectedItem[name] != null) {
              derivedUpdates[name] = {
                modification: 'set',
                value: selectedItem[name],
                modifier_unit: 'number',
              };
            }
          });
        }
      }
    }

    const changeFieldValue = () => (
      this.setState({
        toEdit: {
          ...toEdit,
          [key]: value,
          ...derivedUpdates,
        },
      })
    );

    if (value.modification !== 'set') {
      changeFieldValue();
    }

    validateRow({ [key]: value.value }, meta.currentMeta.columns)
      .then((valid) => {
        if (!valid) return false;
        return changeFieldValue();
      })
      .catch((error) => {
        message.error(error.message);
        return false;
      });
  }

  handleClick = (name) => {
    const { visible } = this.state;
    if (visible === name) return this.hideAll();
    return this.setState({
      visible: name,
    });
  }

  hideAll = () => this.setState({
    visible: '',
  })

  saveResult = () => {
    const {
      ids, meta, adding, onAdd, onBulkEdit,
    } = this.props;
    const { toEdit } = this.state;

    if (adding) { // For adding data.
      onAdd(mapValues(toEdit, ({ value }) => value.id ? value.id : value), () => {
        message.success(ADD_SUCCESS(meta.currentMeta.item_display_name));
      });
    } else { // For bulk editing.
      onBulkEdit({ ids, columns: toEdit }, () => {
        this.clearEdit();
        message.success(BULK_EDIT_SUCCESS({ ids, columns: toEdit }));
      });
    }
  }

  render() {
    const {
      meta, adding, lookups, loading, toCompare,
    } = this.props;
    const { toEdit, visible } = this.state;
    const showableColumns = hideColumns(meta.currentMeta.columns);

    const makeComparable = edit => Object.keys(edit)
      .reduce((a, c) => ({ ...a, [c]: edit[c].value }), {});

    const isDuplicate = edit => toCompare
      && !isEmpty(toCompare.filter(x => isEqual(x, makeComparable(edit))));

    const addDisableText = predicates => predicatesToText(predicates, {
      isDuplicate: IS_DUPLICATE,
      isNotFull: IS_NOT_FULL,
    });

    const editDisableText = predicates => predicatesToText(predicates, {
      isEmpty: IS_EMPTY,
    });

    const getEdit = name => toEdit[name];

    const getLookup = type => type && lookups[snakeToCamel(type)];

    return (
      <Fields compact>
        {showableColumns
          .map(({
            name, type, display_name, lookup, lookup_type, required, editable,
          }) => (
            <Field
              key={name}
              icon={(
                <Icon
                  type={!getEdit(name) ? 'edit' : ''}
                  theme={(adding && required) ? 'filled' : 'outlined'}
                />
              )}
              text={
                (getEdit(name)
                  && buttonText(getEdit(name), getLookup(lookup_type)))
                || <EmptyField>{display_name}</EmptyField>
              }
              lookups={lookups}
              meta={{
                name, type, display: display_name, lookup, lookup_type, required, editable,
              }}
              style={{ width: fieldsWidth(showableColumns) }}
              onEditChange={this.onEditChange}
              adding={adding}
              hideAll={this.hideAll}
              onClick={this.handleClick}
              visible={visible}
            />
          ))}
        <Tooltip
          title={
            adding
              ? addDisableText({
                isDuplicate: isDuplicate(toEdit),
                isNotFull: !isFull(toEdit, showableColumns),
              })
              : editDisableText({
                isEmpty: isEmpty(toEdit),
              })
          }
        >
          <Save
            disabled={
              loading
              || (adding
                ? !isFull(toEdit, showableColumns) || isDuplicate(toEdit)
                : isEmpty(toEdit))
            }
            icon={loading ? 'loading' : 'check'}
            onClick={this.saveResult}
          >
            Save
          </Save>
        </Tooltip>
      </Fields>
    );
  }
}

export default BulkEditor;
